import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, Navigate, useParams } from "react-router-dom";

import api from "../../services/api";
import PagedTable from "../../components/ui/PagedTable";
import ProviderBadge from "../../components/ui/ProviderBadge";
import { notify } from "../../components/ui/Toast";
import { Confirm } from "../../components/ui/panelKit";
import { readApiError } from "../../utils/apiError";
import { exactTime, formatDate, relativeTime } from "../../utils/time";
import { shortAgent } from "../../utils/userAgent";

// "" is the overview, at the bare /shaxslar/<id>.
const TABS = [
  { key: "", label: "Umumiy", icon: "tabler:layout-dashboard" },
  { key: "azolik", label: "Aʼzolik", icon: "tabler:users-group" },
  { key: "xabarlar", label: "Xabarlar", icon: "tabler:message-2" },
  { key: "kirishlar", label: "Kirishlar", icon: "tabler:login-2" },
  { key: "qurilmalar", label: "Qurilmalar", icon: "tabler:device-mobile" },
  { key: "suhbatlar", label: "Shaxsiy suhbatlar", icon: "tabler:messages" },
];

const tabPath = (id, key) => `/shaxslar/${id}${key ? `/${key}` : ""}`;


// ticket_logins.reason, from the gateway (store.TicketRefused*).
const REFUSALS = {
  not_member: "Aʼzo emas yoki olib tashlangan",
  no_name: "Ticketda ism yoʻq",
};

const DEVICE_STATUS = {
  active: { cls: "success", label: "faol" },
  expired: { cls: "neutral", label: "muddati oʻtgan" },
  revoked: { cls: "danger", label: "chiqarilgan" },
};

const when = (t) =>
  t ? (
    <span title={exactTime(t)} style={{ whiteSpace: "nowrap" }}>
      {relativeTime(t)}
    </span>
  ) : (
    "—"
  );

/**
 * One person, in full (2026-10-22): who they are, where they are a member,
 * what they wrote, how they got in, which phones they use and whom they chat
 * with -- and the staff actions on all of it.
 *
 * Staff only, like the Shaxslar list: a PINFL and a person's messages across
 * every client are not a cabinet's business.
 *
 * Actions:
 *   * Vidjetdan chiqarish -- ends every live widget session of this person on
 *     every client; each open widget asks them to log in again.
 *   * Telefonlardan chiqarish -- revokes their app sessions (all, or one
 *     phone in Qurilmalar); the phone must log in with MyID again.
 *   * Aʼzolik: remove one membership, or add a removed one back, through the
 *     same client members API the client page uses (it stamps who did it).
 * The first two are recorded in admin_identity_action and listed on Umumiy.
 */
const PersonPage = () => {
  const { id, tab = "" } = useParams();
  const [state, setState] = useState({ loading: true, error: "" });
  const [person, setPerson] = useState(null);
  const [memberships, setMemberships] = useState(null);
  const [actions, setActions] = useState([]);
  const [logins, setLogins] = useState("");

  // { kind, ...payload } while a confirmation is open.
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  // Bumped after an action, so the open tab's list reloads.
  const [tick, setTick] = useState(0);

  const loadPerson = useCallback(() => {
    return api
      .get(`/identities/${id}/`)
      .then((res) => {
        setPerson(res.data);
        setState({ loading: false, error: "" });
      })
      .catch((err) =>
        setState({ loading: false, error: readApiError(err, {}, "Shaxsni yuklab boʻlmadi.") }),
      );
  }, [id]);

  const loadMemberships = useCallback(() => {
    api
      .get(`/identities/${id}/memberships/`)
      .then((res) => setMemberships(res.data))
      .catch(() => setMemberships([]));
  }, [id]);

  const loadActions = useCallback(() => {
    api
      .get(`/identities/${id}/actions/`)
      .then((res) => setActions(res.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    setState({ loading: true, error: "" });
    loadPerson();
    loadMemberships();
    loadActions();
  }, [loadPerson, loadMemberships, loadActions]);

  const run = async () => {
    setBusy(true);
    try {
      if (confirm.kind === "widget") {
        const res = await api.post(`/identities/${id}/end-widget-sessions/`);
        notify.success(`Vidjet sessiyalari tugatildi: ${res.data.ended} ta.`);
      } else if (confirm.kind === "devices") {
        const res = await api.post(`/identities/${id}/revoke-devices/`, {});
        notify.success(`Telefonlardan chiqarildi: ${res.data.revoked} ta.`);
      } else if (confirm.kind === "device") {
        await api.post(`/identities/${id}/revoke-devices/`, { session_id: confirm.device.id });
        notify.success("Qurilmadan chiqarildi.");
      } else if (confirm.kind === "remove") {
        const m = confirm.membership;
        await api.delete(`/clients/${m.client_id}/members/${person.pinfl}/`, {
          params: { group_id: m.group_id },
        });
        notify.success("Aʼzolikdan olib tashlandi.");
      } else if (confirm.kind === "readd") {
        const m = confirm.membership;
        await api.post(`/clients/${m.client_id}/members/`, {
          pinfl: person.pinfl,
          group_id: m.group_id,
        });
        notify.success("Aʼzolikka qayta qoʻshildi.");
      }
      setConfirm(null);
      setTick((t) => t + 1);
      loadPerson();
      loadMemberships();
      loadActions();
    } catch (err) {
      notify.error(readApiError(err, {}, "Amalni bajarib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  if (!TABS.some((t) => t.key === tab)) {
    return <Navigate to={tabPath(id, "")} replace />;
  }

  if (state.loading) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }

  if (state.error || !person) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{state.error || "Topilmadi."}</span>
        </div>
      </div>
    );
  }

  const live = (memberships || []).filter((m) => !m.removed_at);
  // A removed row can be re-added only if the same group has no live row.
  const liveKey = new Set(live.map((m) => `${m.client_id}/${m.group_id}`));

  const tabCount = {
    azolik: { n: person.membership_count ?? 0 },
    xabarlar: { n: person.message_count },
    kirishlar: person.refused_login_count
      ? { n: person.refused_login_count, danger: true, title: "Rad etilgan kirishlar" }
      : null,
    qurilmalar: { n: person.device_count },
    suhbatlar: { n: person.chat_count },
  };

  const confirmText = {
    widget: {
      title: "Vidjetdan chiqarish",
      message:
        "Bu shaxsning hamma mijoz tizimlaridagi ochiq vidjet sessiyalari tugatiladi. Vidjet qayta kirishni soʻraydi (QR yoki kirish tugmasi). Aʼzolik oʻzgarmaydi.",
      label: "Chiqarish",
    },
    devices: {
      title: "Hamma telefonlardan chiqarish",
      message:
        "MySuhbat ilovasidagi barcha sessiyalari bekor qilinadi — har bir telefon keyingi soʻrovda rad etiladi va MyID bilan qayta kirish kerak boʻladi. Masalan, telefon yoʻqolganda.",
      label: "Chiqarish",
    },
    device: {
      title: "Qurilmadan chiqarish",
      message: "Shu telefon keyingi soʻrovda rad etiladi va MyID bilan qayta kirishi kerak boʻladi.",
      label: "Chiqarish",
    },
    remove: {
      title: "Aʼzolikdan olib tashlash",
      message:
        "Shu guruh boʻyicha kirish huquqi darhol yopiladi. Yozuv tarix uchun saqlanadi. Ticket rejimidagi tizimda ham qayta qoʻshilmaguncha kira olmaydi.",
      label: "Olib tashlash",
    },
    readd: {
      title: "Aʼzolikka qayta qoʻshish",
      message: "Shu guruh boʻyicha kirish huquqi qayta beriladi. Eski yozuv tarixda qoladi.",
      label: "Qoʻshish",
    },
  };
  const ct = confirm ? confirmText[confirm.kind] : null;

  return (
    <>
      <div className="ms-crumbs">
        <Link to="/shaxslar">Shaxslar</Link>
        <Icon icon="tabler:chevron-right" />
        <span>{person.full_name || person.pinfl}</span>
      </div>

      <div className="ms-recordhead">
        <div className="ms-recordhead-meta">
          <h1>{person.full_name || "—"}</h1>
          <span className="ms-mono">{person.pinfl}</span>
        </div>
        <ProviderBadge provider={person.provider} assertedBy={person.asserted_by_name} />
        <div className="ms-toolbar-spacer" />
        <button
          type="button"
          className="ms-btn ms-btn--ghost ms-btn--sm"
          onClick={() => setConfirm({ kind: "widget" })}
        >
          <Icon icon="tabler:browser-x" />
          Vidjetdan chiqarish
        </button>
        <button
          type="button"
          className="ms-btn ms-btn--ghost ms-btn--sm"
          disabled={!person.device_count}
          title={person.device_count ? "" : "Faol telefon yoʻq"}
          onClick={() => setConfirm({ kind: "devices" })}
        >
          <Icon icon="tabler:device-mobile-off" />
          Telefonlardan chiqarish
        </button>
      </div>

      {memberships && live.length === 0 && (
        <div className="ms-alertbar" role="status">
          <Link to={tabPath(id, "azolik")}>
            <Icon icon="tabler:users-minus" />
            <span>Hech bir mijoz tizimida faol aʼzo emas — hech qanday muhokamani oʻqiy olmaydi.</span>
            <b>Aʼzolik →</b>
          </Link>
        </div>
      )}

      <nav className="ms-tabs" aria-label="Boʻlimlar">
        {TABS.map((t) => {
          const c = tabCount[t.key];
          return (
            <NavLink
              key={t.key}
              to={tabPath(id, t.key)}
              end
              className={() => `ms-tab${tab === t.key ? " is-active" : ""}`}
            >
              <Icon icon={t.icon} />
              {t.label}
              {c && (
                <span className={`ms-tab-count${c.danger ? " is-danger" : ""}`} title={c.title}>
                  {c.n}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {tab === "" && (
        <>
          <div className="ms-stats">
            <Link className="ms-stat" to={tabPath(id, "xabarlar")}>
              <div className="ms-stat-icon">
                <Icon icon="tabler:message-2" />
              </div>
              <div className="ms-stat-body">
                <span>Xabarlar</span>
                <b>{person.message_count}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "xabarlar")}>
              <div className="ms-stat-icon">
                <Icon icon="tabler:messages" />
              </div>
              <div className="ms-stat-body">
                <span>Muhokamalar</span>
                <b>{person.thread_count}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "azolik")}>
              <div className={`ms-stat-icon${live.length === 0 ? " is-warn" : ""}`}>
                <Icon icon="tabler:users-group" />
              </div>
              <div className="ms-stat-body">
                <span>Faol aʼzolik</span>
                <b>{person.membership_count ?? 0}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "qurilmalar")}>
              <div className="ms-stat-icon">
                <Icon icon="tabler:device-mobile" />
              </div>
              <div className="ms-stat-body">
                <span>Faol telefonlar</span>
                <b>{person.device_count}</b>
              </div>
            </Link>
            <Link className="ms-stat" to={tabPath(id, "kirishlar")}>
              <div className={`ms-stat-icon${person.refused_login_count ? " is-warn" : ""}`}>
                <Icon icon="tabler:login-2" />
              </div>
              <div className="ms-stat-body">
                <span>Rad etilgan kirishlar</span>
                <b>{person.refused_login_count}</b>
              </div>
            </Link>
          </div>

          <div className="ms-recordgrid">
            <div className="ms-panel">
              <div className="ms-panel-head">
                <Icon icon="tabler:id" />
                <h2>Maʼlumot</h2>
              </div>
              <dl className="ms-facts">
                <div>
                  <dt>Toʻliq ism</dt>
                  <dd>{person.full_name || "—"}</dd>
                </div>
                <div>
                  <dt>PINFL</dt>
                  <dd className="ms-mono">{person.pinfl}</dd>
                </div>
                <div>
                  <dt>Provayder</dt>
                  <dd>
                    <ProviderBadge provider={person.provider} assertedBy={person.asserted_by_name} />
                  </dd>
                </div>
                <div>
                  <dt>Tasdiqlangan</dt>
                  <dd>{when(person.verified_at)}</dd>
                </div>
                <div>
                  <dt>Birinchi kirgan</dt>
                  <dd>{when(person.created_at)}</dd>
                </div>
                <div>
                  <dt>Oxirgi xabar</dt>
                  <dd>{when(person.last_message_at)}</dd>
                </div>
              </dl>
            </div>

            <div className="ms-panel">
              <div className="ms-panel-head">
                <Icon icon="tabler:history" />
                <h2>Amallar tarixi</h2>
              </div>
              {actions.length === 0 ? (
                <div className="ms-state">
                  <Icon icon="tabler:history-off" />
                  <span>Bu shaxsga nisbatan hali amal bajarilmagan.</span>
                </div>
              ) : (
                <div className="ms-table-wrap">
                  <table className="ms-table ms-table--compact">
                    <thead>
                      <tr>
                        <th>Amal</th>
                        <th style={{ width: 90 }}>Soni</th>
                        <th style={{ width: 170 }}>Kim</th>
                        <th style={{ width: 130 }}>Qachon</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actions.map((a, i) => (
                        <tr key={i}>
                          <td className="ms-td-strong">
                            {{
                              revoke_device: "Bitta telefondan chiqarildi",
                              revoke_devices: "Hamma telefonlardan chiqarildi",
                              end_widget_sessions: "Vidjetdan chiqarildi",
                            }[a.kind] || a.kind_label}
                          </td>
                          <td className="ms-td-num">{a.affected}</td>
                          <td className="ms-td-clamp">{a.by}</td>
                          <td className="ms-td-muted">{when(a.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {tab === "azolik" && (
        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:users-group" />
            <h2>Aʼzolik</h2>
            <div className="ms-toolbar-spacer" />
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              Yangi aʼzolik mijoz tizimining Aʼzolar boʻlimidan qoʻshiladi
            </span>
          </div>
          {memberships === null ? (
            <div className="ms-state">
              <div className="ms-spinner" />
            </div>
          ) : memberships.length === 0 ? (
            <div className="ms-state">
              <Icon icon="tabler:users-minus" />
              <span>Hech bir mijoz tizimiga aʼzo boʻlmagan.</span>
            </div>
          ) : (
            <div className="ms-table-wrap">
              <table className="ms-table ms-table--compact">
                <thead>
                  <tr>
                    <th>Mijoz tizimi</th>
                    <th style={{ width: 200 }}>Guruh</th>
                    <th style={{ width: 230 }}>Qoʻshilgan</th>
                    <th style={{ width: 250 }}>Holati</th>
                    <th className="ms-td-actions" style={{ width: 150 }} />
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((m) => (
                    <tr key={m.id}>
                      <td className="ms-td-strong">
                        <Link className="ms-sublink" to={`/mijoz-tizimlari/${m.client_id}/azolar`}>
                          {m.client_name}
                        </Link>
                      </td>
                      <td className="ms-td-clamp">
                        {m.is_root ? "Butun tizim (ildiz)" : m.group_name}
                      </td>
                      <td>
                        {when(m.added_at)}
                        <span className="ms-td-muted">
                          {"\u00a0·\u00a0"}
                          {m.is_auto ? "avto (ticket)" : m.added_by || "—"}
                        </span>
                      </td>
                      <td>
                        {m.removed_at ? (
                          <>
                            <span className="ms-badge ms-badge--danger">olib tashlangan</span>
                            <span className="ms-td-muted">
                              {"\u00a0"}
                              {when(m.removed_at)}
                              {m.removed_by ? `\u00a0·\u00a0${m.removed_by}` : ""}
                            </span>
                          </>
                        ) : (
                          <span className="ms-badge ms-badge--success">faol</span>
                        )}
                      </td>
                      <td className="ms-td-actions">
                        {!m.removed_at ? (
                          <button
                            type="button"
                            className="ms-btn ms-btn--ghost ms-btn--sm"
                            onClick={() => setConfirm({ kind: "remove", membership: m })}
                          >
                            <Icon icon="tabler:user-minus" />
                            Olib tashlash
                          </button>
                        ) : (
                          !liveKey.has(`${m.client_id}/${m.group_id}`) && (
                            <button
                              type="button"
                              className="ms-btn ms-btn--ghost ms-btn--sm"
                              onClick={() => setConfirm({ kind: "readd", membership: m })}
                            >
                              <Icon icon="tabler:user-plus" />
                              Qayta qoʻshish
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "xabarlar" && (
        <div className="ms-panel">
          <PagedTable
            path={`/identities/${id}/messages/`}
            params={{}}
            reloadKey={tick}
            emptyIcon="tabler:message-off"
            empty="Bu shaxs hali hech qanday muhokamada yozmagan."
            head={
              <tr>
                <th>Xabar</th>
                <th style={{ width: 280 }}>Muhokama</th>
                <th style={{ width: 150 }}>Mijoz tizimi</th>
                <th style={{ width: 100 }}>Qayerdan</th>
                <th style={{ width: 130 }}>Vaqt</th>
              </tr>
            }
            row={(m) => (
              <tr key={m.id}>
                <td className="ms-td-clamp">
                  {m.is_deleted ? (
                    <span className="ms-badge ms-badge--danger">oʻchirilgan</span>
                  ) : (
                    <span title={m.preview}>{m.preview}</span>
                  )}
                </td>
                <td className="ms-td-clamp">
                  <Link className="ms-link" to={`/muhokamalar/${m.thread_id}`}>
                    {m.thread_title}
                  </Link>
                  <span className="ms-td-muted"> #{m.seq}</span>
                </td>
                <td className="ms-td-clamp">
                  <Link className="ms-sublink" to={`/mijoz-tizimlari/${m.client_id}`}>
                    {m.client_name}
                  </Link>
                </td>
                <td>
                  <span className="ms-badge ms-badge--neutral">
                    {m.via === "phone" ? "telefon" : "vidjet"}
                  </span>
                </td>
                <td className="ms-td-muted">{when(m.sent_at)}</td>
              </tr>
            )}
          />
        </div>
      )}

      {tab === "kirishlar" && (
        <div className="ms-panel">
          <div className="ms-toolbar">
            <div className="ms-segmented" role="group" aria-label="Natija">
              {[
                ["", "Hammasi"],
                ["1", "Ruxsat berilgan"],
                ["0", "Rad etilgan"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  className={logins === v ? "is-active" : ""}
                  onClick={() => setLogins(v)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="ms-toolbar-spacer" />
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              Ticket orqali kirishlar (mijoz tizimi saytidan)
            </span>
          </div>
          <PagedTable
            path={`/identities/${id}/logins/`}
            params={logins ? { allowed: logins } : {}}
            reloadKey={tick}
            emptyIcon="tabler:login-2"
            empty="Ticket orqali kirish urinishlari yoʻq."
            head={
              <tr>
                <th style={{ width: 150 }}>Vaqt</th>
                <th style={{ width: 160 }}>Mijoz tizimi</th>
                <th style={{ width: 130 }}>Natija</th>
                <th>Sabab</th>
                <th style={{ width: 130 }}>IP</th>
                <th style={{ width: 200 }}>Brauzer</th>
              </tr>
            }
            row={(l) => (
              <tr key={l.id}>
                <td className="ms-td-muted">{when(l.created_at)}</td>
                <td className="ms-td-clamp">
                  <Link className="ms-sublink" to={`/mijoz-tizimlari/${l.client_id}`}>
                    {l.client_name}
                  </Link>
                </td>
                <td>
                  <span className={`ms-badge ms-badge--${l.allowed ? "success" : "danger"}`}>
                    {l.allowed ? "kirdi" : "rad etildi"}
                  </span>
                </td>
                <td className="ms-td-clamp">{l.reason ? REFUSALS[l.reason] || l.reason : "—"}</td>
                <td>
                  <span className="ms-mono">{l.ip || "—"}</span>
                </td>
                <td>
                  <span className="ms-td-muted" title={l.user_agent || ""}>
                    {shortAgent(l.user_agent)}
                  </span>
                </td>
              </tr>
            )}
          />
        </div>
      )}

      {tab === "qurilmalar" && (
        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:device-mobile" />
            <h2>MySuhbat ilovasidagi sessiyalar</h2>
          </div>
          <PagedTable
            path={`/identities/${id}/devices/`}
            params={{}}
            reloadKey={tick}
            emptyIcon="tabler:device-mobile-off"
            empty="Bu shaxs MySuhbat ilovasiga hali kirmagan."
            head={
              <tr>
                <th>Qurilma</th>
                <th style={{ width: 150 }}>Kirgan</th>
                <th style={{ width: 170 }}>Oxirgi yangilanish</th>
                <th style={{ width: 150 }}>Muddati</th>
                <th style={{ width: 140 }}>Holati</th>
                <th className="ms-td-actions" style={{ width: 140 }} />
              </tr>
            }
            row={(d) => {
              const st = DEVICE_STATUS[d.status] || DEVICE_STATUS.expired;
              return (
                <tr key={d.id}>
                  <td className="ms-td-strong ms-td-clamp">{d.device_label || "Nomaʼlum qurilma"}</td>
                  <td className="ms-td-muted">{when(d.created_at)}</td>
                  <td className="ms-td-muted">{when(d.rotated_at)}</td>
                  {/* A future date: relativeTime only speaks of the past. */}
                  <td className="ms-td-muted" title={exactTime(d.expires_at)}>
                    {formatDate(d.expires_at)}
                  </td>
                  <td>
                    <span className={`ms-badge ms-badge--${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="ms-td-actions">
                    {d.status === "active" && (
                      <button
                        type="button"
                        className="ms-btn ms-btn--ghost ms-btn--sm"
                        onClick={() => setConfirm({ kind: "device", device: d })}
                      >
                        <Icon icon="tabler:logout" />
                        Chiqarish
                      </button>
                    )}
                  </td>
                </tr>
              );
            }}
          />
        </div>
      )}

      {tab === "suhbatlar" && (
        <div className="ms-panel">
          <PagedTable
            path={`/identities/${id}/direct-chats/`}
            params={{}}
            reloadKey={tick}
            emptyIcon="tabler:message-off"
            empty="Shaxsiy suhbatlari yoʻq."
            head={
              <tr>
                <th>Suhbatdosh</th>
                <th style={{ width: 170 }}>Mijoz tizimi</th>
                <th style={{ width: 100 }}>Xabarlar</th>
                <th style={{ width: 150 }}>Oxirgi xabar</th>
                <th className="ms-td-actions" style={{ width: 120 }} />
              </tr>
            }
            row={(c) => (
              <tr key={c.id}>
                <td>
                  <div className="ms-namecell">
                    <Link className="ms-sublink" to={`/shaxslar/${c.other.id}`}>
                      <b>{c.other.full_name}</b>
                    </Link>
                    <span>{c.other.pinfl}</span>
                  </div>
                </td>
                <td className="ms-td-clamp">
                  <Link className="ms-sublink" to={`/mijoz-tizimlari/${c.client_id}`}>
                    {c.client_name}
                  </Link>
                </td>
                <td className="ms-td-num">{c.message_count}</td>
                <td className="ms-td-muted">{when(c.last_message_at)}</td>
                <td className="ms-td-actions">
                  <Link className="ms-btn ms-btn--ghost ms-btn--sm" to={`/shaxsiy-suhbatlar/${c.id}`}>
                    Ochish
                    <Icon icon="tabler:chevron-right" />
                  </Link>
                </td>
              </tr>
            )}
          />
        </div>
      )}

      <Confirm
        open={confirm !== null}
        busy={busy}
        name={person.full_name || person.pinfl}
        title={ct?.title}
        message={ct?.message}
        confirmLabel={ct?.label}
        cancelLabel="Bekor qilish"
        busyLabel="Bajarilmoqda..."
        onConfirm={run}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
};

export default PersonPage;
